import { CommonModule } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NGXLogger as LoggerService } from 'ngx-logger';

import { EmbedModeService } from '../../../shared/services/embed-mode.service';
import { GoogleAnalyticsService } from '../../../shared/services/google-analytics.service';
import { getEffectiveLang, normalizeLanguage } from '../../../shared/services/lang-pref';
import { LanguagePreferenceService } from '../../../shared/services/language-preference.service';
import { NavigationService } from '../../../whoo/core/navigation.service';
import { FadeInAndOutComponent } from '../../../shared/fade-in-and-out/fade-in-and-out.component';
import { LANGUAGE_BANNER_TEXTS, LanguageBannerText } from './language-banner.texts';

/** Zeitpunkt des Hinweises: spaet genug fuer LCP/CLS, frueh genug vor dem Absprung. */
const SHOW_AFTER_MS = 2 * 1000;

/**
 * Hinweis (kein Zwang), wenn die Sprache der URL nicht zur effektiven Sprache des
 * Besuchers passt — der Normalfall bei geteilten Poll-Links: der deutsche Ersteller
 * teilt `/de/vote/...`, der englische Empfaenger landet auf Deutsch.
 *
 * Ersetzt den frueheren 30-Sekunden-Dialog. Zwei Unterschiede sind der eigentliche
 * Fix: der Hinweis kommt nach 2 s statt 30 s (vorher war ein grosser Teil der Nutzer
 * schon weg), und gespeichert wird die ANTWORT statt des Anzeigens. Frueher setzte
 * der Dialog sein Flag in ngOnInit — er erschien damit genau einmal pro Browser,
 * egal ob bestaetigt, abgebrochen oder weggeklickt, und kam nie wieder.
 *
 * Die URL bleibt die einzige Quelle der Wahrheit fuers Rendering: kein Auto-Redirect,
 * geteilte Links bleiben deterministisch.
 */
@Component({
    selector: 'language-banner',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatIconModule, FadeInAndOutComponent],
    templateUrl: './language-banner.component.html',
    styleUrls: ['./language-banner.component.scss'],
})
export class LanguageBannerComponent implements OnInit, OnDestroy {
    /** Sprache der aktuellen URL = Sprache des laufenden Builds. */
    public urlLanguage: string;
    /** Sprache, in die gewechselt werden kann (Praeferenz oder Browsersprache). */
    public targetLanguage: string;
    private timerId: any;
    private conditionsMet = false;

    constructor(
        @Inject(LOCALE_ID) private locale: string,
        private languagePreference: LanguagePreferenceService,
        private navigation: NavigationService,
        private embedMode: EmbedModeService,
        private googleAnalytics: GoogleAnalyticsService,
        private logger: LoggerService,
    ) { }

    ngOnInit(): void {
        if (this.embedMode.isEmbedded) {
            // Im Embed-Mode (iframe) ist die App-Chrome sowieso unterdrueckt.
            return;
        }
        this.urlLanguage = normalizeLanguage(this.locale);
        this.targetLanguage = getEffectiveLang();
        this.timerId = setTimeout(() => {
            this.conditionsMet = this.shouldShow();
            this.logger.log('LanguageBanner', this.urlLanguage, this.targetLanguage, this.conditionsMet);
        }, SHOW_AFTER_MS);
    }

    ngOnDestroy(): void {
        clearTimeout(this.timerId);
    }

    private shouldShow(): boolean {
        if (!this.urlLanguage || !this.targetLanguage) {
            return false;
        }
        if (this.urlLanguage === this.targetLanguage) {
            return false;
        }
        if (!LANGUAGE_BANNER_TEXTS[this.targetLanguage]) {
            return false;
        }
        return !this.languagePreference.isDismissed(this.urlLanguage, this.targetLanguage);
    }

    /**
     * Der Cookie-Consent belegt dieselbe Ecke unten. Solange er offen ist, warten
     * wir — sobald er beantwortet ist, erscheint der Sprachhinweis.
     */
    public get isVisible(): boolean {
        return this.conditionsMet && !this.googleAnalytics.showCookieConsent;
    }

    public get texts(): LanguageBannerText {
        return LANGUAGE_BANNER_TEXTS[this.targetLanguage] ?? LANGUAGE_BANNER_TEXTS['en'];
    }

    /** Wechseln: Praeferenz merken und die aktuelle Route im Ziel-Build oeffnen. */
    public async switchLanguage(): Promise<void> {
        this.conditionsMet = false;
        await this.languagePreference.choose(this.targetLanguage);
        this.navigation.openWindowLanguage(this.targetLanguage);
    }

    /** Ablehnen: gilt nur fuer genau dieses Sprachpaar, ein anderes fragt wieder. */
    public dismiss(): void {
        this.languagePreference.dismiss(this.urlLanguage, this.targetLanguage);
        this.conditionsMet = false;
    }
}
